/**
 * threatEffects.js v1.0.0 - 2026-10-13 (Tab 2 plan, step 7: threat assessment, LOC, air and weather, each wired once)
 *
 * What the Tab 2 threat, LOC, air situation and weather inputs do to the numbers (docs/planning/TAB2_STEP7_PLAN.md, docs/planning/TAB2_THREAT_AUDIT.md).
 * Tables of the options stay in threatAssessment.js, locConfig.js, airSituation.js and operationsConfig.js; this file holds the rules
 * that combine them. Every number added here is labelled; the sourced tables are used as they are.
 *
 *  1. ROAD STATE of an evacuation / supply leg from Role 2 upward: MSR, ASR, bridge switch, LOC security, insurgent activity, IED
 *     threat and the operation weather -> speed factor, availability, delay, open / degraded / closed.
 *  2. HELICOPTER AVAILABILITY: air situation ceiling x enemy air defense x weather (relative to clear).
 *  3. REAR AND SUPPORT casualty factor: artillery x insurgent activity (the drone rear factor already exists in the battle module).
 *  4. DOW factor of the threat: IED and CBRN (the larger of the two; the drone DOW modifier is applied separately where it already is).
 *  5. THREAT TYPE: DNBI factor only.
 */
import { ROAD_STATUS, LOC_SECURITY, BRIDGE_STATUS, WEATHER_ROUTE_IMPACTS } from './locConfig';
import { INSURGENT_ACTIVITY, IED_THREAT_LEVELS, ENEMY_AD_LEVELS, ARTILLERY_THREAT, CBRN_RISK, THREAT_TYPES, DRONE_THREAT } from './threatAssessment';
import { WEATHER_CONDITIONS } from './operationsConfig';
import { AIR_SITUATION_LEVELS } from './airSituation';
import { getInfrastructureModifiers } from './infrastructureStatus';

export const THREAT_EFFECTS_USE = true;   // false restores the former behaviour (threat panel without effect except the drone)

// ---------------------------------------------------------------------------------------------------------------
// 1. Road state
// ---------------------------------------------------------------------------------------------------------------
/** Operation weather (WEATHER_CONDITIONS keys) -> route weather (WEATHER_ROUTE_IMPACTS keys). */
export const WEATHER_TO_ROUTE_WEATHER = Object.freeze({
  CLEAR: 'CLEAR', RAIN: 'RAIN', SNOW: 'SNOW', EXTREME_COLD: 'EXTREME_COLD', EXTREME_HEAT: 'CLEAR', FOG: 'FOG', ADVERSE: 'HEAVY_RAIN', RASPUTITSA: 'RASPUTITSA'
});

/**
 * 2026-10-20 (R2-11, option C chosen by Dr. Tatar 2026-10-18: road surface x weather): true = the speed factor of the weather on the route in use
 * is the weather's road_impact for that surface (locConfig WEATHER_ROUTE_IMPACTS[*].road_impact: paved, improved, unpaved; a DAMAGED road counts
 * as unpaved), instead of one speed modifier for every surface. Mud on an unpaved road then stops convoys, rain on a paved road costs little.
 * The values are the existing table (educated guess). false = one weather speed modifier for all surfaces, as before.
 */
export const ROAD_SURFACE_X_WEATHER_ON = true;
const SURFACE_OF_STATUS = Object.freeze({ PAVED: 'PAVED', IMPROVED: 'IMPROVED', UNPAVED: 'UNPAVED', DAMAGED: 'UNPAVED' });

/** Operation weather -> weather key of the resupply convoy table (ResupplyTransportService WEATHER_IMPACT). EDUCATED GUESS. */
export const WEATHER_TO_RESUPPLY_WEATHER = Object.freeze({
  CLEAR: 'CLEAR', RAIN: 'RAIN', SNOW: 'SNOW', EXTREME_COLD: 'SNOW', EXTREME_HEAT: 'CLEAR', FOG: 'FOG', ADVERSE: 'HEAVY_RAIN', RASPUTITSA: 'RASPUTITSA'
});

/** Operation weather -> weather key of the evacuation capacity tables (evacuationCapacityCalculator WEATHER_MODIFIERS). EDUCATED GUESS (R2-18, W2). */
export const WEATHER_TO_CAPACITY_WEATHER = Object.freeze({
  CLEAR: 'CLEAR', RAIN: 'LIGHT_RAIN', SNOW: 'SNOW', EXTREME_COLD: 'SNOW', EXTREME_HEAT: 'CLEAR', FOG: 'FOG', ADVERSE: 'HEAVY_RAIN', RASPUTITSA: 'HEAVY_RAIN'
});

/** Share of convoys and ambulances that get through in a route weather (1 = no effect). EDUCATED GUESS from the table risks. */
export const ROUTE_WEATHER_AVAILABILITY = Object.freeze({
  CLEAR: 1.0, RAIN: 0.9, HEAVY_RAIN: 0.7, SNOW: 0.7, RASPUTITSA: 0.5, FOG: 0.9, DUST_STORM: 0.8, EXTREME_COLD: 0.8
});

/** How much of the insurgent penalty the road security stops (1 = all). EDUCATED GUESS. */
export const LOC_SECURITY_STRENGTH = Object.freeze({ SECURE: 0.8, GUARDED: 0.6, CONTESTED: 0.3, INTERDICTED: 0.1, DENIED: 0.0 });

/** The alternate route is longer than the main route (time x). EDUCATED GUESS. */
export const ASR_DETOUR_FACTOR = 1.3;

/** Floor of speed x availability (a degraded leg never takes more than 1 / floor times its normal time). */
export const ROAD_FLOOR = 0.1;

export const ROAD_STATE_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Speed, delay and ambush probability of the security level, the IED and weather tables are the existing sourced-or-guess tables of locConfig.js and threatAssessment.js; availability = (1 - ambush probability) x insurgent penalty softened by the security strength x weather share (guess); road surface is not part of it (separate discussion).'
});

const roadLeg = (key, label) => {
  const road = ROAD_STATUS[key];
  if (!road) throw new Error(`threatEffects.resolveRoadState: unknown ${label} status '${key}' (valid: ${Object.keys(ROAD_STATUS).join(', ')})`);
  return road;
};

/**
 * @param {Object} c
 * @param {Object} c.locConfig            operation_parameters.loc_config (msr_status, asr_status, loc_security, primary_bridge)
 * @param {Object} c.threatAssessment     operation_parameters.threat_assessment (ied_threat, insurgent_activity)
 * @param {string} c.weather              operation weather (WEATHER_CONDITIONS key)
 */
export function resolveRoadState({ locConfig = {}, threatAssessment = {}, weather = 'CLEAR' }) {
  const msrKey = locConfig.msr_status || 'PAVED';
  const asrKey = locConfig.asr_status || 'UNPAVED';
  const secKey = locConfig.loc_security || 'SECURE';
  const bridgeKey = locConfig.primary_bridge || 'INTACT';
  const iedKey = threatAssessment.ied_threat || 'NONE';
  const insKey = threatAssessment.insurgent_activity || 'NONE';
  const sec = LOC_SECURITY[secKey];
  if (!sec) throw new Error(`threatEffects.resolveRoadState: unknown LOC security '${secKey}' (valid: ${Object.keys(LOC_SECURITY).join(', ')})`);
  const bridge = BRIDGE_STATUS[bridgeKey];
  if (!bridge) throw new Error(`threatEffects.resolveRoadState: unknown bridge status '${bridgeKey}' (valid: ${Object.keys(BRIDGE_STATUS).join(', ')})`);
  const ied = IED_THREAT_LEVELS[iedKey];
  if (!ied) throw new Error(`threatEffects.resolveRoadState: unknown IED threat '${iedKey}' (valid: ${Object.keys(IED_THREAT_LEVELS).join(', ')})`);
  const ins = INSURGENT_ACTIVITY[insKey];
  if (!ins) throw new Error(`threatEffects.resolveRoadState: unknown insurgent activity '${insKey}' (valid: ${Object.keys(INSURGENT_ACTIVITY).join(', ')})`);
  const routeWeatherKey = WEATHER_TO_ROUTE_WEATHER[weather];
  if (!routeWeatherKey) throw new Error(`threatEffects.resolveRoadState: unknown weather '${weather}' (valid: ${Object.keys(WEATHER_TO_ROUTE_WEATHER).join(', ')})`);
  const routeWeather = WEATHER_ROUTE_IMPACTS[routeWeatherKey];

  const msr = roadLeg(msrKey, 'MSR');
  const asr = roadLeg(asrKey, 'ASR');
  // the condition counts here (damaged slows, denied closes); the surface x weather part is below (ROAD_SURFACE_X_WEATHER_ON)
  const conditionSpeed = (r) => (r.value === 'DENIED' ? 0 : r.value === 'DAMAGED' ? r.speed_modifier : 1.0);
  const bridgeUsable = bridge.usable !== false;   // 'CONDITIONAL' (damaged bridge) is usable with a delay
  const msrOpen = conditionSpeed(msr) > 0 && bridgeUsable;
  const asrOpen = conditionSpeed(asr) > 0;
  const route = msrOpen ? 'MSR' : (asrOpen ? 'ASR' : 'NONE');
  const condSpeed = route === 'MSR' ? conditionSpeed(msr) : route === 'ASR' ? conditionSpeed(asr) : 0;
  const detour = route === 'ASR' ? 1 / ASR_DETOUR_FACTOR : 1.0;
  const bridgeDelayMin = (route === 'MSR' && bridge.delay_per_vehicle_seconds) ? Math.round(bridge.delay_per_vehicle_seconds / 60 * 10) / 10 : 0;

  const surface = route === 'MSR' ? SURFACE_OF_STATUS[msrKey] : route === 'ASR' ? SURFACE_OF_STATUS[asrKey] : null;
  const weatherSpeed = (ROAD_SURFACE_X_WEATHER_ON && surface && typeof routeWeather.road_impact?.[surface] === 'number')
    ? routeWeather.road_impact[surface] : routeWeather.speed_modifier;
  const speedFactor = route === 'NONE' ? 0
    : Math.max(ROAD_FLOOR, condSpeed * detour * sec.speed_modifier * weatherSpeed * ied.ground_evac_speed_modifier);
  const strength = LOC_SECURITY_STRENGTH[secKey] ?? 0;
  const insurgentPenalty = 1 - (1 - ins.loc_security_modifier) * (1 - strength);
  const availability = route === 'NONE' ? 0
    : Math.max(ROAD_FLOOR, (1 - sec.ambush_probability) * insurgentPenalty * ROUTE_WEATHER_AVAILABILITY[routeWeatherKey]);
  const delayMin = route === 'NONE' ? 0 : (Number.isFinite(sec.delay_minutes) ? sec.delay_minutes : 0) + (ied.evacuation_delay_minutes || 0) + bridgeDelayMin;
  const status = route === 'NONE' ? 'CLOSED'
    : (speedFactor * availability >= 0.8 ? 'OPEN' : 'DEGRADED');
  return {
    status, route, speed_factor: speedFactor, availability, delay_min: delayMin,
    surface, weather_speed: weatherSpeed,
    parts: { msr: msrKey, asr: asrKey, bridge: bridgeKey, security: secKey, insurgent: insKey, ied: iedKey, weather: routeWeatherKey, insurgent_penalty: insurgentPenalty, security_strength: strength }
  };
}

/**
 * Extra minutes on the legs above Role 2 (from the base minutes of those legs): time / (speed x availability) + delay.
 * A closed leg uses the floor (a tenfold time) so the result is finite and visible; the status says CLOSED.
 */
export function rearLegExtraMinutes(roadState, baseLegMinutes) {
  if (typeof baseLegMinutes !== 'number' || !Number.isFinite(baseLegMinutes) || baseLegMinutes < 0) {
    throw new Error(`threatEffects.rearLegExtraMinutes: baseLegMinutes must be a non-negative number (got ${baseLegMinutes})`);
  }
  if (baseLegMinutes === 0) return 0;
  const f = roadState.status === 'CLOSED' ? ROAD_FLOOR : Math.max(ROAD_FLOOR, roadState.speed_factor * roadState.availability);
  return Math.round(baseLegMinutes * (1 / f - 1) + roadState.delay_min);
}

// ---------------------------------------------------------------------------------------------------------------
// 2. Helicopter availability
// ---------------------------------------------------------------------------------------------------------------
/** Ceiling of the theater air situation on helicopter MEDEVAC. EDUCATED GUESS (Dr. Tatar 2026-10-13: enemy air superiority 0.1). */
export const AIR_SITUATION_CEILING = Object.freeze({ AIR_SUPERIORITY: 1.0, PARITY: 0.8, CONTESTED: 0.4, ENEMY_AIR_SUPERIORITY: 0.1 });

export const HELICOPTER_MODEL_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Air situation ceiling (guess) x enemy air defense MEDEVAC availability (table of threatAssessment.js) x weather helicopter availability relative to clear (table of operationsConfig.js).'
});

export function helicopterAvailability({ airSituationLevel = 'PARITY', enemyAd = 'NONE', weather = 'CLEAR', coordination = 1 }) {
  const ceiling = AIR_SITUATION_CEILING[airSituationLevel];
  if (ceiling === undefined) throw new Error(`threatEffects.helicopterAvailability: unknown air situation '${airSituationLevel}' (valid: ${Object.keys(AIR_SITUATION_CEILING).join(', ')})`);
  const ad = ENEMY_AD_LEVELS[enemyAd];
  if (!ad) throw new Error(`threatEffects.helicopterAvailability: unknown enemy air defense '${enemyAd}' (valid: ${Object.keys(ENEMY_AD_LEVELS).join(', ')})`);
  const w = WEATHER_CONDITIONS[weather];
  const clear = WEATHER_CONDITIONS.CLEAR;
  if (!w) throw new Error(`threatEffects.helicopterAvailability: unknown weather '${weather}' (valid: ${Object.keys(WEATHER_CONDITIONS).join(', ')})`);
  const weatherFactor = w.helicopter_availability / clear.helicopter_availability;
  // coordination: MEDEVAC coordination of the infrastructure card (communications 1.0 to 0.1)
  return { value: ceiling * ad.medevac_availability * weatherFactor * coordination, parts: { air_situation: ceiling, enemy_air_defense: ad.medevac_availability, weather: weatherFactor, coordination } };
}
export const AIR_SITUATION_KEYS = Object.keys(AIR_SITUATION_LEVELS);

// ---------------------------------------------------------------------------------------------------------------
// 3. Rear and support casualty factor, 4. DOW factor, 5. DNBI factor of the threat type
// ---------------------------------------------------------------------------------------------------------------
export function rearThreatFactor(threatAssessment = {}) {
  const art = ARTILLERY_THREAT[threatAssessment.artillery_threat || 'LOW'];
  const ins = INSURGENT_ACTIVITY[threatAssessment.insurgent_activity || 'NONE'];
  if (!art) throw new Error(`threatEffects.rearThreatFactor: unknown artillery threat '${threatAssessment.artillery_threat}'`);
  if (!ins) throw new Error(`threatEffects.rearThreatFactor: unknown insurgent activity '${threatAssessment.insurgent_activity}'`);
  return { value: art.casualty_modifier * ins.rear_area_casualty_modifier, parts: { artillery: art.casualty_modifier, insurgent: ins.rear_area_casualty_modifier } };
}

export function threatDowFactor(threatAssessment = {}) {
  const ied = IED_THREAT_LEVELS[threatAssessment.ied_threat || 'NONE'];
  const cbrn = CBRN_RISK[threatAssessment.cbrn_risk || 'NONE'];
  if (!ied) throw new Error(`threatEffects.threatDowFactor: unknown IED threat '${threatAssessment.ied_threat}'`);
  if (!cbrn) throw new Error(`threatEffects.threatDowFactor: unknown CBRN risk '${threatAssessment.cbrn_risk}'`);
  return { value: Math.max(ied.dow_modifier || 1.0, cbrn.dow_modifier || 1.0), parts: { ied: ied.dow_modifier || 1.0, cbrn: cbrn.dow_modifier || 1.0 } };
}
export const CBRN_STATUS = Object.freeze({ status: 'NO_DATA', basis: 'No data found for the CBRN effect on died of wounds; the table values (x1.2 moderate, x1.5 high) are educated guesses and the casualty addition is not applied (depends on the agent).' });

export function threatTypeDnbiFactor(threatAssessment = {}) {
  const t = THREAT_TYPES[threatAssessment.threat_type || 'CONVENTIONAL'];
  if (!t) throw new Error(`threatEffects.threatTypeDnbiFactor: unknown threat type '${threatAssessment.threat_type}'`);
  return t.dnbi_modifier ?? 1.0;
}

/** IED casualties on march, transit and convoy days: the march battle rate times the IED casualty modifier (hazard, not resistance). */
export function iedMarchFactor(threatAssessment = {}) {
  const ied = IED_THREAT_LEVELS[threatAssessment.ied_threat || 'NONE'];
  if (!ied) throw new Error(`threatEffects.iedMarchFactor: unknown IED threat '${threatAssessment.ied_threat}'`);
  return ied.casualty_modifier || 1.0;
}

// ---------------------------------------------------------------------------------------------------------------
// 6. Supply on the same roads (Tab 2 plan 7.3)
// ---------------------------------------------------------------------------------------------------------------
/** Hours a ground resupply needs on the legs from Role 2 upward under normal conditions (EDUCATED GUESS; the road state scales it). */
export const SUPPLY_BASE_TRANSIT_HOURS = 12;

/**
 * A ground resupply event under the road state: the share that arrives (availability) and the days it is late
 * (transit time over the speed factor, plus the delay, rounded to whole days). Air and drone events are not on the roads.
 * The drone threat adds a supply vehicle loss like the MEDEVAC vehicle loss: weekly rate (from the threat table) applied by week, floor 0.3.
 */
export function supplyDelivery(roadState, { day = 1, droneWeeklyLoss = 0, method = 'GROUND' } = {}) {
  const ground = !method || method === 'GROUND' || method === 'ROAD';
  if (!ground || !roadState) return { fraction: 1, delay_days: 0 };
  const weeks = Math.floor((day - 1) / 7);
  const droneFactor = droneWeeklyLoss > 0 ? Math.max(0.30, Math.pow(1 - droneWeeklyLoss, weeks)) : 1;
  const f = roadState.status === 'CLOSED' ? ROAD_FLOOR : Math.max(ROAD_FLOOR, roadState.speed_factor);
  const extraHours = SUPPLY_BASE_TRANSIT_HOURS * (1 / f - 1) + roadState.delay_min / 60;
  return {
    fraction: roadState.status === 'CLOSED' ? ROAD_FLOOR * droneFactor : roadState.availability * droneFactor,
    delay_days: Math.round(extraHours / 24)
  };
}

// ---------------------------------------------------------------------------------------------------------------
// 7. Infrastructure card (Tab 2 plan 7.10): power, communications, water, local medical
// ---------------------------------------------------------------------------------------------------------------
/** The modifiers of the infrastructure card when it is enabled, else null. Power and water -> surgical capacity; communications -> evacuation delay and
 *  MEDEVAC coordination; water -> DNBI; local medical -> host nation beds. Blood storage, imaging and fuel are not wired yet. */
export function infrastructureModel(infrastructureConfig) {
  if (!THREAT_EFFECTS_USE || !infrastructureConfig || infrastructureConfig.enabled !== true) return null;
  return getInfrastructureModifiers(infrastructureConfig);
}

// ---------------------------------------------------------------------------------------------------------------
// 8. What the threat card really applies (for its chips; Tab 2 plan 7.7)
// ---------------------------------------------------------------------------------------------------------------
export function appliedThreatSummary(threatAssessment = {}) {
  const ta = threatAssessment;
  const drone = DRONE_THREAT[ta.drone_threat || 'NONE'];
  const ad = ENEMY_AD_LEVELS[ta.enemy_ad || 'NONE'];
  const ied = IED_THREAT_LEVELS[ta.ied_threat || 'NONE'];
  const cbrnDnbi = ta.cbrn_risk === 'LOW' ? 1.15 : ta.cbrn_risk === 'MODERATE' ? 1.35 : ta.cbrn_risk === 'HIGH' ? 1.6 : 1.0;   // CBRN_THREAT_LEVELS.dnbi_multiplier (cbrnMedical.js)
  return {
    front: drone ? drone.casualty_modifier : 1.0,
    rear: rearThreatFactor(ta).value,
    dow: threatDowFactor(ta).value * (drone ? (drone.dow_modifier || 1.0) : 1.0),
    dnbi: threatTypeDnbiFactor(ta) * cbrnDnbi,
    helicopter_ad: ad ? ad.medevac_availability : 1.0,
    mascal_probability: ARTILLERY_THREAT[ta.artillery_threat || 'LOW']?.mass_casualty_probability || 0,
    no_golden_hour: Boolean(ad && ad.medevac_availability <= 0.3) || Boolean(ied && ied.value === 'EXTREME')
  };
}
